/*============================================================================
  Ractive Core — your own "brain".
  Replaces a remote, license-locked animation engine with self-hosted code.
  No external requests, no tokens, no server dependency.
  Config is injected via window.RactiveCore in theme.liquid.
  © Ractiveone. Clean-room implementation, free to modify.
==============================================================================*/
(function () {
  'use strict';

  var cfg = window.RactiveCore || {};
  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var inEditor = window.Shopify && window.Shopify.designMode;

  /* ----------------------------------------------------------------------
     Scroll reveal animations
     - Reveals elements with [data-ractive-reveal] / .ractive-reveal
     - "animate all" mode auto-targets the outermost sections in <main>
     -------------------------------------------------------------------- */
  function collectTargets() {
    var explicit = Array.prototype.slice.call(
      document.querySelectorAll('[data-ractive-reveal], .ractive-reveal')
    );

    if (!cfg.animateAll) return explicit;

    var sections = Array.prototype.slice.call(
      document.querySelectorAll('#MainContent .shopify-section')
    ).filter(function (el) {
      // outermost sections only — skip nested ones to avoid double animating
      return !el.parentElement || !el.parentElement.closest('.shopify-section');
    });

    return explicit.concat(sections);
  }

  function prep(el) {
    if (el.dataset.ractiveReady) return;
    el.dataset.ractiveReady = '1';
    el.classList.add('ractive-reveal-init');
    if (!el.getAttribute('data-ractive-anim')) {
      el.setAttribute('data-ractive-anim', cfg.animationStyle || 'fade-up');
    }
    var delay = el.getAttribute('data-ractive-delay');
    if (delay) el.style.setProperty('--ractive-reveal-delay', parseInt(delay, 10) + 'ms');
  }

  function reveal(el) {
    el.classList.add('is-revealed');
  }

  function initReveal() {
    if (!cfg.animations || prefersReduced) return;

    var targets = collectTargets();
    if (!targets.length) return;

    // In the theme editor, show everything immediately so merchants can edit.
    if (inEditor || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { prep(el); reveal(el); });
      return;
    }

    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          reveal(entry.target);
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    targets.forEach(function (el) { prep(el); io.observe(el); });
  }

  /* ----------------------------------------------------------------------
     Scroll-to-top button
     -------------------------------------------------------------------- */
  function initToTop() {
    var btn = document.querySelector('[data-ractive-to-top]');
    if (!btn) return;

    var threshold = parseInt(cfg.toTopThreshold, 10) || 600;
    var ticking = false;

    function update() {
      btn.classList.toggle('is-visible', window.pageYOffset > threshold);
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });

    btn.addEventListener('click', function () {
      window.scrollTo({
        top: 0,
        behavior: prefersReduced ? 'auto' : 'smooth'
      });
    });

    update();
  }

  /* ----------------------------------------------------------------------
     Boot
     -------------------------------------------------------------------- */
  function boot() {
    initReveal();
    initToTop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-scan when sections are added/reordered in the theme editor.
  if (inEditor) {
    document.addEventListener('shopify:section:load', initReveal);
    document.addEventListener('shopify:section:reorder', initReveal);
  }
})();
